import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { forwardRef, useState, useImperativeHandle } from "react";
import CancelIcon from "../../../assets/icons/cross-bold-icon.png";
import GlobalButton from "../../../components/button";
import Label from "../../../components/label";
import TextInput from "../../../components/textInput";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { addTarrifValidation } from "../../../utils/Validation";
import {
  createBroadBand,
  updateBroadBand,
} from "../../../services/BroadbandApi";
import AppLoader from "./../../../components/loader/index";
import { handleApiCall } from "../../../utils/helper";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": { padding: theme.spacing(2) },
  "& .MuiPaper-root": {
    width: "600px",
    [theme.breakpoints.down("sm")]: { margin: 12 },
  },
}));

const AddEditServiceDialog = forwardRef(({}, ref) => {
  const [open, setOpen] = useState(false);
  const [dialogData, setDialogData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [inputValues, setInputValues] = useState({
    type: "",
    value: "",
    description: "",
    salePrice: "",
    buyPrice: "",
    highPrice: "",
    length12: "",
    length18: "",
    length24: "",
    length36: "",
  });
  const [inputError, setInputError] = useState({
    type: false,
    value: false,
    description: false,
    salePrice: false,
    buyPrice: false,
    highPrice: false,
    length12: false,
    length18: false,
    length24: false,
    length36: false,
  });

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
      setDialogData(params);

      if (params.type === "edit") {
        const { data } = params;

        setInputValues({
          type: data?.type,
          value: data?.value,
          description: data?.description,
          salePrice: data?.salePrice,
          buyPrice: data?.buyPrice,
          highPrice: data?.highPrice,
          length12: data?.length12,
          length18: data?.length18,
          length24: data?.length24,
          length36: data?.length36,
        });
      }
    },
    disable() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputValues({});
  };

  const handleChangeInputValue = (event) => {
    const { name, value } = event.target;
    setInputError((prev) => ({ ...prev, [name]: false }));
    setInputValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddEditTarrif = async () => {
    const validate = addTarrifValidation(inputValues, setInputError);
    if (validate) {
      return;
    }

    let payload = {
      type: dialogData?.value,
      value: inputValues?.value,
      description: inputValues?.description,
      buyPrice: inputValues.buyPrice,
      salePrice: inputValues?.salePrice,
      highPrice: inputValues.highPrice,
      length12: inputValues.length12,
      length18: inputValues.length18,
      length24: inputValues.length24,
      length36: inputValues.length36,
      isAdditional: true,
    };

    if (dialogData?.type === "add") {
      handleApiCall(
        "",
        createBroadBand,
        payload,
        setIsLoading,
        handleClose,
        dialogData.getData
      );
    } else {
      handleApiCall(
        dialogData?.data?._id,
        updateBroadBand,
        payload,
        setIsLoading,
        handleClose,
        dialogData.getData
      );
    }
  };

  return (
    <BootstrapDialog onClose={handleClose} open={open}>
      <AppLoader isVisible={isLoading} />
      <Stack
        p={2}
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Typography
          variant="h1"
          fontSize={"1.5rem"}
          fontWeight={500}
          color={"secondary.dark"}
        >
          {dialogData?.type === "add" ? "Add Service" : "Edit Service"}
        </Typography>
        <IconButton onClick={handleClose}>
          <img src={CancelIcon} alt="cancel-icon" width={25} />
        </IconButton>
      </Stack>
      <DialogContent dividers>
        <Box margin={"auto"}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Label title={"Service Name"} required htmlFor={"customer-name"} />
              <TextInput
                id={"customer-name"}
                type={"text"}
                required
                error={inputError.value}
                helperText={inputError.value && "Service name is required"}
                defaultStyle={true}
                placeholder={"Enter service name"}
                value={inputValues.value}
                name={"value"}
                onChange={handleChangeInputValue}
              />
            </Grid>
            <Grid item xs={12}>
              <Label
                title={"Short Description"}
                required
                htmlFor={"short-description"}
              />
              <TextInput
                id={"short-description"}
                type={"text"}
                required
                error={inputError.description}
                helperText={inputError.description && "Service description is required"}
                defaultStyle={true}
                placeholder={"Enter service description"}
                value={inputValues.description}
                name={"description"}
                onChange={handleChangeInputValue}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Label title={"Buy Price"} required htmlFor={"buy-price"} />
              <TextInput
                id={"buy-price"}
                type={"number"}
                required
                error={inputError.buyPrice}
                helperText={inputError.buyPrice && "Buy price is required"}
                defaultStyle={true}
                placeholder={"Enter buy price"}
                value={inputValues.buyPrice}
                name={"buyPrice"}
                onChange={handleChangeInputValue}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Label title={"Sale Price"} required htmlFor={"sale-price"} />
              <TextInput
                id={"sale-price"}
                type={"number"}
                required
                error={inputError.salePrice}
                helperText={inputError.salePrice && "Sale price is required"}
                defaultStyle={true}
                placeholder={"Enter sale price"}
                value={inputValues.salePrice}
                name={"salePrice"}
                onChange={handleChangeInputValue}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Label title={"High Price"} required htmlFor={"high-price"} />
              <TextInput
                id={"high-price"}
                type={"number"}
                required
                error={inputError.highPrice}
                helperText={inputError.highPrice && "High price is required"}
                defaultStyle={true}
                placeholder={"Enter high price"}
                value={inputValues.highPrice}
                name={"highPrice"}
                onChange={handleChangeInputValue}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Label
                title={"Price ( Length 12 )"}
                required
                htmlFor={"length12-price"}
              />
              <TextInput
                id={"length12-price"}
                type={"number"}
                required
                error={inputError.length12}
                helperText={
                  inputError.length12 &&
                  "Price for Contract length 12 is required"
                }
                defaultStyle={true}
                placeholder={"Enter lenght 12 price"}
                value={inputValues.length12}
                name={"length12"}
                onChange={handleChangeInputValue}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Label
                title={"Price ( Length 18 )"}
                required
                htmlFor={"length18-price"}
              />
              <TextInput
                id={"length18-price"}
                type={"number"}
                required
                error={inputError.length18}
                helperText={
                  inputError.length18 &&
                  "Price for Contract length 18 is required"
                }
                defaultStyle={true}
                placeholder={"Enter length 18 price"}
                value={inputValues.length18}
                name={"length18"}
                onChange={handleChangeInputValue}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Label
                title={"Price ( Length 24 )"}
                required
                htmlFor={"length24-price"}
              />
              <TextInput
                id={"length24-price"}
                type={"number"}
                required
                error={inputError.length24}
                helperText={
                  inputError.length24 &&
                  "Price for Contract length 24 is required"
                }
                defaultStyle={true}
                placeholder={"Enter length 24 price"}
                value={inputValues.length24}
                name={"length24"}
                onChange={handleChangeInputValue}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Label
                title={"Price ( Length 36 )"}
                required
                htmlFor={"length36-price"}
              />
              <TextInput
                id={"length36-price"}
                type={"number"}
                required
                error={inputError.length36}
                helperText={
                  inputError.length36 &&
                  "Price for Contract length 36 is required"
                }
                defaultStyle={true}
                placeholder={"Enter length 36 price"}
                value={inputValues.length36}
                name={"length36"}
                onChange={handleChangeInputValue}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <GlobalButton
          variant={"custom_gradient"}
          handleClickBtn={handleClose}
          title={"Cancel"}
          style={{
            width: 100,
            background: "red !important",
          }}
        />
        <GlobalButton
          variant={"custom_gradient"}
          title={dialogData?.type === "add" ? "Add" : "Update"}
          style={{ width: 100 }}
          handleClickBtn={handleAddEditTarrif}
        />
      </DialogActions>
    </BootstrapDialog>
  );
});

export default AddEditServiceDialog;
